import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { SITE } from "../data/site";

const TEXTURE =
  "https://images.unsplash.com/photo-1760172287483-02d382f63a6f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

function Chapter({ ch, i }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 85%", "start 25%"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [40, 0]);
  const opacity = useTransform(scrollYProgress, [0, 0.6, 1], [0, 1, 1]);

  return (
    <motion.div
      ref={ref}
      style={{ y, opacity }}
      className="grid grid-cols-12 gap-6 md:gap-10 py-14 md:py-20 border-t border-white/10"
      data-testid={`manifesto-chapter-${ch.no}`}
    >
      <div className="col-span-12 md:col-span-3 flex items-start gap-4">
        <span className="font-mono text-[11px] tracking-[0.3em] text-[var(--gold)] pt-3">
          CHAPTER
        </span>
        <span className="font-display text-6xl md:text-7xl leading-none text-[var(--gold)]">
          {ch.no}
        </span>
      </div>
      <div className="col-span-12 md:col-span-5">
        <h3 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-[-0.01em]">
          {ch.title}
        </h3>
      </div>
      <div className="col-span-12 md:col-span-4">
        <p className="text-[15px] md:text-[16px] leading-[1.65] text-[var(--ink-dim)]">
          {ch.body}
        </p>
      </div>
    </motion.div>
  );
}

export default function About() {
  const ref = useRef(null);

  return (
    <section
      ref={ref}
      id="about"
      className="relative py-24 md:py-40 px-6 md:px-10"
      data-testid="about-section"
    >
      <div className="mx-auto max-w-[1600px]">
        {/* Section label */}
        <div className="grid grid-cols-12 gap-6 items-end mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-6">
            <div className="flex items-center gap-4 text-[11px] tracking-[0.32em] uppercase text-[var(--ink-dim)] mb-6">
              <span className="w-10 h-px bg-[var(--gold)]" />
              <span className="text-[var(--gold)] font-mono">02</span>
              <span>Our Manifesto</span>
            </div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.98] tracking-[-0.02em]">
              A studio for
              <br />
              <span className="italic text-[var(--gold)] font-light">
                three hundred and sixty degree
              </span>
              <br />
              brand building.
            </h2>
          </div>

          <div className="col-span-12 md:col-span-5 md:col-start-8">
            <p className="text-[16px] md:text-[18px] leading-[1.6] text-[var(--ink-dim)]">
              We are not a marketing agency. We are the operating partner behind
              brands that intend to matter, architected across strategy,
              distribution, community and coaching. What we build is engineered
              to outlast its launch.
            </p>
            <blockquote className="mt-10 pt-6 border-t border-white/10">
              <p className="font-display text-2xl md:text-3xl leading-[1.25] italic text-[var(--ink)]">
                “A brand is not what you say it is.
                <br />
                It is what compounds after you leave the room.”
              </p>
              <footer className="mt-4 text-[11px] tracking-[0.3em] uppercase text-[var(--ink-mute)]">
                Dinkar Chopra, Founding Partner
              </footer>
            </blockquote>
          </div>
        </div>

        {/* Editorial texture / accent row (no portraits) */}
        <div className="grid grid-cols-12 gap-6 md:gap-8 mb-6 md:mb-10">
          <div className="col-span-12 md:col-span-5 flex flex-col gap-6 md:gap-8">
            <div className="border border-white/10 p-6 md:p-8 bg-[var(--bg-2)]/60 flex-1 flex flex-col justify-between min-h-[220px]">
              <div className="font-mono text-[10px] tracking-[0.3em] text-[var(--gold)]">
                PHILOSOPHY
              </div>
              <p className="font-display text-2xl md:text-3xl leading-[1.2] mt-6">
                Diagnosis before prescription.
                <span className="text-[var(--ink-dim)]"> Every engagement begins in the founder’s office — not in a slide deck.</span>
              </p>
            </div>
            <div className="border border-white/10 p-6 md:p-8 bg-[var(--bg-2)]/60 flex-1 flex flex-col justify-between min-h-[220px]">
              <div className="font-mono text-[10px] tracking-[0.3em] text-[var(--gold)]">
                METHOD
              </div>
              <p className="font-display text-2xl md:text-3xl leading-[1.2] mt-6">
                Identify · Ideate · Execute · Scale.
                <span className="text-[var(--ink-dim)]"> Four movements. One accountability.</span>
              </p>
            </div>
          </div>

          <div className="col-span-12 md:col-span-7 aspect-[16/11] md:aspect-auto relative overflow-hidden border border-white/10">
            <img
              src={TEXTURE}
              alt=""
              className="w-full h-full object-cover"
              draggable={false}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[var(--bg)]/20 to-[var(--bg)]/80" />
            <div className="absolute inset-0 p-8 md:p-12 flex flex-col justify-between">
              <div className="flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-[var(--ink)]">
                <span className="font-mono text-[var(--gold)]">Plate · 01</span>
                <span>One Playbook</span>
              </div>
              <div>
                <p className="font-display text-3xl md:text-5xl leading-[1.02] max-w-[18ch]">
                  Built for founders who intend to outlive the trend cycle.
                </p>
                <div className="mt-6 flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-[var(--ink-dim)]">
                  <span className="w-8 h-px bg-[var(--gold)]" />
                  <span>Vol. XXX</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Manifesto chapters */}
        <div className="mt-16 md:mt-24">
          <div className="flex items-baseline justify-between mb-4">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--ink-mute)]">
              The Four Movements
            </div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--ink-mute)] hidden md:block">
              04 · Chapters
            </div>
          </div>
          {SITE.manifesto.map((ch, i) => (
            <Chapter ch={ch} i={i} key={ch.no} />
          ))}
        </div>
      </div>
    </section>
  );
}
